import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BellRing, Search } from "lucide-react";

import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Loading } from "../../components/shared";

const TYPES = ["NOTICE", "CIRCULAR", "ANNOUNCEMENT"];
const priorities = {
  NORMAL: "bg-slate-100 text-slate-700",
  IMPORTANT: "bg-amber-100 text-amber-800",
  URGENT: "bg-red-100 text-red-800",
};

export default function ResidentNotices() {
  const [items, setItems] = useState(null);
  const [filters, setFilters] = useState({
    q: "",
    notice_type: "",
    category: "",
  });

  useEffect(() => {
    const timer = window.setTimeout(() => {
      api
        .get("/resident-communications/notices", { params: filters })
        .then((response) => {
          setItems(response.data);
        })
        .catch(() => setItems([]));
    }, 180);
    return () => window.clearTimeout(timer);
  }, [filters]);

  if (!items) return <Loading />;

  return (
    <div data-testid="resident-notices-page">
      <header className="border-b border-slate-200 pb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
          Society communications
        </p>
        <h1 className="mt-2 font-display text-4xl font-bold text-slate-900">
          Notices & Circulars
        </h1>
      </header>
      <section
        className="mt-6 border border-slate-200 bg-white p-4"
        data-testid="resident-notice-filters"
      >
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="relative">
            <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <Input
              className="pl-9"
              data-testid="resident-notice-search"
              onChange={(event) =>
                setFilters({ ...filters, q: event.target.value })
              }
              placeholder="Search notices"
              value={filters.q}
            />
          </label>
          <select
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            data-testid="resident-notice-type-filter"
            onChange={(event) =>
              setFilters({ ...filters, notice_type: event.target.value })
            }
            value={filters.notice_type}
          >
            <option value="">All types</option>
            {TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          <Input
            data-testid="resident-notice-category-filter"
            onChange={(event) =>
              setFilters({ ...filters, category: event.target.value })
            }
            placeholder="Filter category"
            value={filters.category}
          />
        </div>
      </section>
      <section className="mt-6 space-y-3" data-testid="resident-notice-list">
        {items.length ? (
          items.map((item) => (
            <Link
              className="block border border-slate-200 bg-white p-5 transition-colors hover:border-blue-500 hover:bg-blue-50"
              data-testid={`resident-notice-${item.notice_id}`}
              key={item.notice_id}
              to={`/resident/notices/${item.notice_id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-blue-700">
                      {item.type}
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-semibold ${priorities[item.priority]}`}
                    >
                      {item.priority.toLowerCase()}
                    </span>
                    {!item.read && (
                      <span className="rounded-full bg-blue-700 px-2 py-0.5 text-xs font-semibold text-white">
                        New
                      </span>
                    )}
                  </div>
                  <h2 className="mt-2 font-display text-xl font-semibold text-slate-900">
                    {item.title}
                  </h2>
                  <p className="mt-1 text-sm text-slate-600">
                    {item.subject || item.category}
                  </p>
                </div>
                <p className="text-xs text-slate-500">
                  {(item.publish_at || "").slice(0, 10)}
                </p>
              </div>
              <p className="mt-4 line-clamp-2 text-sm leading-6 text-slate-700">
                {item.body}
              </p>
              <div className="mt-4 flex flex-wrap gap-3 text-xs text-slate-500">
                {item.attachment_count > 0 && (
                  <span>{item.attachment_count} attachment(s)</span>
                )}
                {item.acknowledgement_required && (
                  <span className="font-semibold text-amber-800">
                    Acknowledgement required
                  </span>
                )}
              </div>
            </Link>
          ))
        ) : (
          <div
            className="border border-dashed border-slate-300 bg-white p-7 text-sm text-slate-600"
            data-testid="resident-notice-empty"
          >
            <BellRing className="mb-3 h-5 w-5 text-slate-500" />
            No notices match your current Society and home access.
          </div>
        )}
      </section>
    </div>
  );
}
